/**
 * Helper functions for ChallengeInfo component
 * Contains utility functions for challenge data formatting and calculations
 */

import React from 'react'
import { t } from 'ttag'
import { msgid, ngettext } from 'ttag'
import { GoalWithProgress, CustomType, Lang } from '../../services/api.types'
import { convertUTCToLocalDate, getPeriodString } from '../../common/helpers/date'
import { calculateDuration } from '../ChallengeAdmin/ChallengeAdmin'
import { formatThousands } from '../../common/helpers/translations'
import { unitsCountPointsShortFunc } from '../../common/helpers/units'
import { CustomTypeCategory, getCustomTypeTextById, getCustomUnitText } from '../../services/CustomTypes'
import { isSimpleDayType } from '../../common/helpers/challenges'
import { PointsLogic } from '../ChallengeAdmin/components/ChallengePoints/ChallengePoints'

// Labels for daily and total limits
export const dailyMaxLabel = t`Daily limit per user`
export const totalMaxLabel = t`Total limit per user`

/**
 * Calculate period string with days count
 * @param goal - The goal with start/end dates
 * @returns Formatted string like "Jan 1 - Jan 31 (31 days)"
 */
export const getPeriodWithDays = (goal: GoalWithProgress): string => {
  const startDate = convertUTCToLocalDate(new Date(goal.startDate))
  const endDate = convertUTCToLocalDate(new Date(goal.endDate))
  const days = calculateDuration(startDate, endDate)
  const daysText = days ? ngettext(msgid`day`, `days`, days) : ''
  return getPeriodString(goal.startDate, goal.endDate) + ` (${days} ${daysText})`
}

/**
 * Checks if activity category is a custom type
 */
export const isCustomActivityCategory = (category: CustomTypeCategory): boolean => {
  return [
    CustomTypeCategory.CUSTOM_SIMPLE,
    CustomTypeCategory.CUSTOM_WITH_AMOUNT,
    CustomTypeCategory.BONUS_SIMPLE,
    CustomTypeCategory.BONUS_WITH_AMOUNT,
  ].includes(category)
}

/**
 * Checks if activity category is bonus points
 */
export const isBonusPointsCategory = (category: CustomTypeCategory): boolean => {
  return [CustomTypeCategory.BONUS_SIMPLE, CustomTypeCategory.BONUS_WITH_AMOUNT].includes(category)
}

/**
 * Checks if activity category is a simple custom type
 */
export const isCustomSimpleCategory = (category: CustomTypeCategory): boolean => {
  return [CustomTypeCategory.CUSTOM_SIMPLE, CustomTypeCategory.BONUS_SIMPLE].includes(category)
}

/**
 * Get the activity unit text based on category
 */
export const getActivityUnitText = (activity: PointsLogic): string => {
  const isCustom = isCustomActivityCategory(activity.category)
  const isCustomSimple = isCustomSimpleCategory(activity.category)

  if (isCustom) {
    return isCustomSimple ? t`Done`.toLowerCase() : activity.customUnitName
  }
  return getCustomUnitText(activity.unit)
}

/**
 * Calculate daily max points with optional rounding
 */
export const calculateDailyMaxPoints = (activity: PointsLogic, roundDailyPoints: boolean): number => {
  if (!activity.dailyGoal) return 0
  let dailyMax = (activity.dailyGoal * activity.points) / activity.value
  if (roundDailyPoints) dailyMax = Math.floor(dailyMax)
  return dailyMax
}

/**
 * Calculate total max points
 */
export const calculateTotalMaxPoints = (activity: PointsLogic): number => {
  if (!activity.totalMax) return 0
  return (activity.totalMax * activity.points) / activity.value
}

/**
 * Determines if daily max should be shown
 */
export const shouldShowDailyMax = (
  activity: PointsLogic,
  isDayView: boolean | undefined,
  dailyMaxExceeded: boolean,
): boolean => {
  return Boolean(
    activity.dailyGoal &&
      activity.category !== CustomTypeCategory.CUSTOM_SIMPLE &&
      !isSimpleDayType(activity.category) &&
      (!isDayView || dailyMaxExceeded),
  )
}

/**
 * Determines if total max should be shown
 */
export const shouldShowTotalMax = (
  totalMax: number,
  isDayView: boolean | undefined,
  totalMaxExceeded: boolean,
): boolean => {
  return Boolean(totalMax && totalMax > 0 && (!isDayView || totalMaxExceeded))
}

/**
 * Get activity label with bonus indicator if applicable
 */
export const getActivityLabel = (activity: PointsLogic): JSX.Element => {
  const isCustom = isCustomActivityCategory(activity.category)
  const isBonusPoints = isBonusPointsCategory(activity.category)
  const labelText = isCustom ? activity.customActivityName : getCustomTypeTextById(activity.category)

  return (
    <>
      {labelText as string}
      {isBonusPoints && <span style={{ fontSize: '11px', fontWeight: 400 }}> ({t`Bonus points`.toLowerCase()})</span>}
    </>
  )
}

/**
 * Format points formula content for display
 */
export const formatPointsContent = (
  value: string,
  unit: string,
  pointsAmount: string,
  pointsUnit: string,
  showDailyMax: boolean,
  dailyMax: number,
  maxPointsUnit: string,
  dailyMaxExceeded: boolean,
  showTotalMax: boolean,
  totalMax: number,
  totalMaxUnit: string,
  totalMaxExceeded: boolean,
): JSX.Element => {
  return (
    <>
      <div style={{ marginBottom: '2px' }}>{`${value} ${unit} = ${pointsAmount} ${pointsUnit}`}</div>
      {showDailyMax && (
        <div style={{ opacity: 0.7, fontSize: '11px' }}>
          {`${dailyMaxLabel}: ${formatThousands(dailyMax)} ${maxPointsUnit}${dailyMaxExceeded ? ' (' + t`exceeded` + ')' : ''}`}
        </div>
      )}
      {showTotalMax && (
        <div style={{ opacity: 0.7, fontSize: '11px' }}>
          {`${totalMaxLabel}: ${formatThousands(totalMax)} ${totalMaxUnit}${totalMaxExceeded ? ' (' + t`exceeded` + ')' : ''}`}
        </div>
      )}
    </>
  )
}
